import { scheduleGenerationEditorState } from "..";
import { mutatorAction } from "satcheljs";
import { extendObservable } from "mobx";
import ScheduleGenerationEditorStateSchema from "../store/schema/ScheduleGenerationEditorStateSchema";
import ISmartScheduleShiftEntity, { WeekDays } from "sh-models/shift/ISmartScheduleShiftEntity";
import SmartScheduleShiftEntity from "sh-models/shift/SmartScheduleShiftEntity";
import RestClient from "sh-rest-client";
import { IShiftBlockDefinition, IShiftBlocksRequest } from "sh-rest-client/ShiftBlocksRequest";
import { TeamStore } from "sh-team-store";
import { trace } from "owa-trace";
import type { Moment } from "moment";

const SHIFT_BLOCK_TIME_FORMAT = "HH:mm:ss";
const ALL_WEEK_DAYS: WeekDays[] = [
    WeekDays.Monday,
    WeekDays.Tuesday,
    WeekDays.Wednesday,
    WeekDays.Thursday,
    WeekDays.Friday,
    WeekDays.Saturday,
    WeekDays.Sunday
];

export const setIsAutoSchedulePanelOpen = mutatorAction("isAutoSchedulePanelOpen", function setIsAutoSchedulePanelOpen(isOpen: boolean) {
    scheduleGenerationEditorState().isAutoSchedulePanelOpen = isOpen;
});

export const setIsAddShiftBlockPanelOpen = mutatorAction("isAddShiftBlockPanelOpen", function setIsAddShiftBlockPanelOpen(isOpen: boolean) {
    scheduleGenerationEditorState().isAddShiftBlockPanelOpen = isOpen;
});

export const setSelectedGroupId = mutatorAction("setSelectedGroupId", function setSelectedGroup(id: string) {
    scheduleGenerationEditorState().selectedGroupId = id;
});

export const setShiftInEdit = mutatorAction("setShiftInEdit", function setShiftInEdit(store: ScheduleGenerationEditorStateSchema, shift: ISmartScheduleShiftEntity) {
    if (store) {
        store.shiftInEdit = shift;
    }
});

export const saveNewShiftBlock = mutatorAction("saveNewShiftBlock", function saveNewShiftBlock(newShift: ISmartScheduleShiftEntity) {
    if (!newShift) {
        return;
    }
    if (!newShift.id) {
        newShift.id = SmartScheduleShiftEntity.generateNewSmartScheduleShiftBlockId();
    }
    // Make the new shift observable before adding to MobX store to ensure reactivity
    const observableShift = extendObservable(newShift, {...newShift});
    scheduleGenerationEditorState().shiftBlocks.push(observableShift);
});

export const persistAllShiftBlocks = mutatorAction("persistAllShiftBlocks", function persistAllShiftBlocks() {
    void persistShiftBlocksCollection();
});

export const updateSlotsSmartScheduleShiftById = mutatorAction("updateSlotsSmartScheduleShiftById", function updateSlotsSmartScheduleShiftById (slots: number, shiftId: string) {
    const index = scheduleGenerationEditorState().shiftBlocks.findIndex(shift => shift.id === shiftId);
    if (index !== -1) {
        scheduleGenerationEditorState().shiftBlocks[index].openSlots = slots;
    }
});

export const updateShiftBlock = mutatorAction("updateShiftBlock", function updateShiftBlock(id: string, updatedShift: ISmartScheduleShiftEntity) {
    const index = scheduleGenerationEditorState().shiftBlocks.findIndex(shift => shift.id === id);
    if (index !== -1) {
        scheduleGenerationEditorState().shiftBlocks[index] = updatedShift;
    }
});

export const deleteShiftBlock = mutatorAction("deleteShiftBlock", function deleteShiftBlock(shiftId: string) {
    const index = scheduleGenerationEditorState().shiftBlocks.findIndex(shift => shift.id === shiftId);
    if (index !== -1) {
        scheduleGenerationEditorState().shiftBlocks.splice(index, 1);
    }
});

export const updateRepeatOnShiftBlock = mutatorAction("updateRepeatOnShiftBlock", function updateRepeatOnShiftBlock(shiftId: string, repeatOn: any[]) {
    const index = scheduleGenerationEditorState().shiftBlocks.findIndex(shift => shift.id === shiftId);
    if (index !== -1) {
        scheduleGenerationEditorState().shiftBlocks[index].repeatOn = repeatOn;
    }
});

export const resetScheduleGenerationEditorState = mutatorAction("resetScheduleGenerationEditorState", function resetScheduleGenerationEditorState() {
    const state = scheduleGenerationEditorState();
    state.isAddShiftBlockPanelOpen = false;
    state.selectedGroupId = undefined;
    state.shiftInEdit = undefined;
    state.shiftBlocks = [];
    state.shiftBlocksSnapshot = undefined;
});

async function persistShiftBlocksCollection(): Promise<void> {
    const state = scheduleGenerationEditorState();
    const shiftBlocks = state.shiftBlocks || [];

    const teamId = TeamStore().teamId;
    const tagId = scheduleGenerationEditorState().selectedGroupId;

    if (!teamId || !tagId) {
        trace.warn("persistShiftBlocksCollection called without required identifiers");
        return;
    }

    const definitionsWithLocalIds = shiftBlocks
        .map(shift => {
            const definition = buildShiftBlockDefinition(shift);
            if (!definition) {
                trace.warn(`persistShiftBlocksCollection unable to build definition for shift ${shift.id}`);
                return null;
            }

            return {
                localId: shift.id!,
                definition
            };
        })
        .filter((entry): entry is { localId: string; definition: IShiftBlockDefinition } => !!entry);

    const request: IShiftBlocksRequest = {
        tagId,
        shiftblocks: definitionsWithLocalIds.map(entry => entry.definition)
    };

    try {
        const response = await RestClient.addShiftBlocks(teamId, request);
        const persistedBlocks = response?.shiftBlocks || [];
        if (persistedBlocks.length === 0) {
            trace.warn("persistShiftBlocksCollection received empty persisted blocks response. All shift blocks removed");
            return;
        }
        // Update snapshot after successful save
        setShiftBlocksSnapshot(shiftBlocks);
    } catch (error: any) {
        trace.warn(`persistShiftBlocksCollection failed: ${error?.message || error}`);
    }
}

function ensureShiftHasId(shift: ISmartScheduleShiftEntity): string {
    if (!shift.id) {
        shift.id = SmartScheduleShiftEntity.generateNewSmartScheduleShiftBlockId();
    }

    return shift.id;
}

function buildShiftBlockDefinition(shift: ISmartScheduleShiftEntity): IShiftBlockDefinition | null {
    if (!shift.startTime || !shift.endTime) {
        trace.warn("buildShiftBlockDefinition called without start/end times");
        return null;
    }

    const repeatDays = (shift.repeatOn && shift.repeatOn.length > 0 ? shift.repeatOn : ALL_WEEK_DAYS) as WeekDays[];
    const repeatDayStrings = repeatDays.map(day => day as string);
    const breaksPayload = (shift.breaks || []).filter(brk => typeof brk?.duration === "number" && !!brk.breakType).map(brk => ({
        duration: brk.duration,
        breakType: brk.breakType
    }));
    const subshiftPayload = (shift.subshifts || [])
        .filter(sub => !!sub?.startTime && !!sub?.endTime)
        .map(sub => ({
            title: sub.title || "",
            startTime: formatShiftTimeToUtcString(sub.startTime!),
            endTime: formatShiftTimeToUtcString(sub.endTime!),
            theme: sub.theme,
            subshiftType: sub.subshiftType
        }));

    const definition: IShiftBlockDefinition = {
        id: ensureShiftHasId(shift),
        eTag: typeof shift.eTag === "string" && shift.eTag.length > 0 ? shift.eTag : null,
        title: shift.title,
        startTime: formatShiftTimeToUtcString(shift.startTime),
        endTime: formatShiftTimeToUtcString(shift.endTime),
        slots: typeof shift.openSlots === "number" ? shift.openSlots : 0,
        repeatEveryday: repeatDays.length === ALL_WEEK_DAYS.length,
        repeatDays: repeatDayStrings,
        notes: shift.notes ?? null,
        theme: shift.theme ?? null,
        breaks: breaksPayload.length ? breaksPayload : undefined,
        subshifts: subshiftPayload.length ? subshiftPayload : undefined
    };

    return definition;
}

export const setShiftBlocks = mutatorAction("setShiftBlocks", function setShiftBlocks(shiftBlocks: ISmartScheduleShiftEntity[]) {
    scheduleGenerationEditorState().shiftBlocks = shiftBlocks;
});

export const setShiftBlocksSnapshot = mutatorAction("setShiftBlocksSnapshot", function setShiftBlocksSnapshot(shiftBlocks: ISmartScheduleShiftEntity[]) {
    const snapshot = createShiftBlocksSnapshot(shiftBlocks);
    scheduleGenerationEditorState().shiftBlocksSnapshot = snapshot;
});

function createShiftBlocksSnapshot(shiftBlocks: ISmartScheduleShiftEntity[]): string {
    // Create a stable hash of the shift blocks by serializing relevant properties
    const normalized = shiftBlocks.map(shift => ({
        id: shift.id,
        eTag: shift.eTag,
        title: shift.title,
        startTime: shift.startTime ? shift.startTime.valueOf() : null,
        endTime: shift.endTime ? shift.endTime.valueOf() : null,
        openSlots: shift.openSlots,
        repeatOn: shift.repeatOn ? [...shift.repeatOn].sort() : null,
        notes: shift.notes,
        theme: shift.theme,
        breaks: shift.breaks,
        subshifts: shift.subshifts
    }));
    return JSON.stringify(normalized);
}

export function hasUnsavedShiftBlockChanges(): boolean {
    const state = scheduleGenerationEditorState();
    const currentSnapshot = createShiftBlocksSnapshot(state.shiftBlocks || []);
    return state.shiftBlocksSnapshot !== currentSnapshot;
}

function formatShiftTimeToUtcString(time: Moment): string {
    // Clone to avoid mutating the original moment instance before converting to UTC
    return time.clone().utc().format(SHIFT_BLOCK_TIME_FORMAT);
}